import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "primereact/button";
import DashboardLayout from "../../layouts/DashboardLayout";
import Loading from "../../../../components/Loading/Loading";
import useDashboardData from "../../hooks/useDashboardData";
import useCurrency from "../../../../hooks/useCurrency";
import { formatPrice } from "../../../../utilities/formatPrice";
import localeText from "../../../../locale";
import PendingExtracreditReceipts from "./components/PendingExtracreditReceipts/PendingExtracreditReceipts";
import ExtracreditPowerOfUse from "./components/ExtracreditPowerOfUse/ExtracreditPowerOfUse";
import ExtracreditOpportunities from "./components/ExtracreditOpportunities/ExtracreditOpportunities";
import ExtracreditMiniStatement from "./components/ExtracreditMiniStatement/ExtracreditMiniStatement";
import TransferExtracreditCard from "./components/TransferExtracreditCard/TransferExtracreditCard";
import QuickAccessCard from "../CustomerDashboard/components/QuickAccessCard";
import QrCodePayDialog from "../../components/QrCodePay/QrCodePayDialog";

const ExtracreditPage = ({ userInfo, userTypeInfo, currency: currencyCode }) => {
  const { data, isLoading, error, refreshData } = useDashboardData();
  const { currencySymbol } = useCurrency(data?.walletInfo?.currency || currencyCode);

  const extracoinInfo = data?.extracoinInfo;
  const balance = extracoinInfo?.balance ?? 0;
  const purchasingPowerEquivalent = balance * 0.5;
  const extracoinPercentage = data?.extracoinPercentage ?? 0;
  const [qrPayVisible, setQrPayVisible] = useState(false);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <Loading classNameIcon="text-8xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-screen">
          <p className="text-red-500">Erro ao carregar dados.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <nav className="flex align-items-center gap-2 mb-2 text-sm text-600" aria-label="Breadcrumb">
        <Link to="/dashboard-financeiro" className="text-600 hover:text-primary">
          {localeText("financial")}
        </Link>
        <span>/</span>
        <span style={{ color: "var(--theme-text, #344563)" }}>{localeText("extracredit")}</span>
      </nav>

      <div className="mb-4">
        <h2 className="text-lg font-semibold m-0" style={{ color: "var(--theme-text, #344563)" }}>
          {localeText("extracredit")}
        </h2>
        <p className="text-sm text-600 m-0 mt-1">{localeText("financialHubSubtitle")}</p>
      </div>

      <PendingExtracreditReceipts
        currencySymbol={currencySymbol}
        onRefresh={refreshData}
        userTypeInfo={userTypeInfo}
      />

      <div className="grid m-0 mb-4">
        <div
          className="card border-round-md col-12 md:col-8 p-3 border-none"
          style={{
            backgroundColor: "var(--theme-primary-dark, #346958)",
            color: "var(--theme-primary-light, #FFFFFF)",
          }}
        >
          <div className="h-full flex flex-column justify-content-between flex-wrap gap-3">
            <div>
              <div className="mb-2 flex align-items-center gap-2">
                <i
                  className="pi pi-receipt text-xs text-lg"
                  style={{ color: "var(--theme-primary-light, #F1FBF7)" }}
                />{" "}
                <span className="text-xs" style={{ color: "var(--theme-primary-light, #ACE6B8)" }}>
                  {localeText("availableBalance").toUpperCase()}
                </span>
              </div>
              <div
                className="text-4xl font-bold mb-1"
                style={{ color: "rgba(255, 255, 255, 0.95)" }}
              >
                {balance} <span className="text-sm font-medium">ECR</span>
              </div>
              {/*  <div className="text-sm opacity-80">
                ≈ {currencySymbol} {formatPrice(purchasingPowerEquivalent)}{" "}
                {localeText("purchasingPower")}
              </div> */}
            </div>
            <div className="flex gap-2">
              <Link to="/shop">
                <Button
                  label={localeText("useInStore")}
                  icon="pi pi-shopping-bag"
                  className="border-round-md border-none p-no-outline"
                  style={{
                    backgroundColor: "var(--theme-primary, #1DAF61)",
                    color: "#FFFFFF",
                  }}
                />
              </Link>
              <Link to="/dashboard-financeiro/extracredit/transferir">
                <Button
                  label={localeText("transfer")}
                  icon="pi pi-arrow-right-arrow-left"
                  className="border-round-md p-button-text p-no-outline"
                  style={{
                    border: "1px solid rgba(255, 255, 255, 0.95)",
                    color: "rgba(255, 255, 255, 0.95)",
                  }}
                />
              </Link>
            </div>
          </div>
        </div>

        <div className="col-12 md:col-4 p-0 md:pl-3">
          <ExtracreditPowerOfUse
            extracoinPercentage={extracoinPercentage}
            userTypeInfo={userTypeInfo}
          />
        </div>
      </div>
      <ExtracreditOpportunities
        extracoinPercentage={extracoinPercentage}
        currencySymbol={currencySymbol}
      />

      <div className="mb-4">
        <div className="font-semibold mb-3 m-0" style={{ color: "var(--theme-text, #172B4D)" }}>
          {localeText("howToUseCredits")}
        </div>
        <div className="flex gap-2 flex-wrap mt-2">
          <TransferExtracreditCard />
          <QuickAccessCard
            onClick={() => setQrPayVisible(true)}
            icon="pi pi-qrcode"
            label={localeText("payWithQrCode") || "Paga con QR Code"}
          />
        </div>
      </div>
      {userInfo && (
        <QrCodePayDialog
          visible={qrPayVisible}
          onHide={() => setQrPayVisible(false)}
          user={userInfo}
          currency={currencySymbol}
        />
      )}

      <ExtracreditMiniStatement currency={currencySymbol} />
    </DashboardLayout>
  );
};

export default ExtracreditPage;
